import React from "react";
import { Button, Card, Col, Row, Typography, Drawer, Modal, Descriptions, Tag, List, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";

import { AdminMenuLayout } from "../../../shared/components";
import { TablePromotions } from "./TablePromotions";
import { PromotionForm } from "./form";
import { usePromotions, IPromotionRule } from "../hooks/usePromotions";
import { useUpdatePromotion } from "../hooks/useUpdatePromotion";

const { Text } = Typography;

const PROMO_TYPE_LABELS: Record<string, string> = {
  "2x1": "2x1 (Lleva 2, paga 1)",
  "3x2": "3x2 (Lleva 3, paga 2)",
  custom: "Personalizada",
};

const DAY_LABELS: Record<string, string> = {
  monday: "Lunes",
  tuesday: "Martes",
  wednesday: "Miércoles",
  thursday: "Jueves",
  friday: "Viernes",
  saturday: "Sábado",
  sunday: "Domingo",
};

export const Promotions: React.FC = () => {
  const [searchParams] = useSearchParams();
  const promoTypeFilter = searchParams.get("promoType");

  const [openCreateDrawer, setOpenCreateDrawer] = React.useState(false);
  const [editingPromotion, setEditingPromotion] =
    React.useState<IPromotionRule | null>(null);
  const [selectedPromotion, setSelectedPromotion] =
    React.useState<IPromotionRule | null>(null);
  const [viewModalOpen, setViewModalOpen] = React.useState(false);

  const { promotions, loading, refresh } = usePromotions();
  const { updateStatus, removePromotion } = useUpdatePromotion({ onSuccess: refresh });

  const filteredPromotions = React.useMemo(() => {
    if (!promoTypeFilter) {
      return promotions;
    }
    return promotions.filter(
      (promotion) => promotion.promo_type === promoTypeFilter,
    );
  }, [promotions, promoTypeFilter]);

  const handleView = (promotion: IPromotionRule) => {
    setSelectedPromotion(promotion);
    setViewModalOpen(true);
  };

  const handleEdit = (promotion: IPromotionRule) => {
    setEditingPromotion(promotion);
  };

  const closeEditDrawer = () => {
    setEditingPromotion(null);
  };

  const closeCreateDrawer = () => {
    setOpenCreateDrawer(false);
  };

  const getDaysActive = (promotion: IPromotionRule) => {
    const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    return days
      .filter((day) => promotion[day as keyof IPromotionRule] === "true")
      .map((day) => DAY_LABELS[day]);
  };

  return (
    <AdminMenuLayout pageDescription="Promociones" title="Promociones">
      <Card bodyStyle={{ padding: "24px", display: "block" }} style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <div style={{ 
          marginBottom: 24, 
          display: "flex", 
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #f0f0f0",
          paddingBottom: 16
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0, color: "#262626" }}>
            {promoTypeFilter
              ? `Promociones ${PROMO_TYPE_LABELS[promoTypeFilter] ?? promoTypeFilter}`
              : "Listado de Promociones"}
          </h2>
          
          <Space size={12}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setOpenCreateDrawer(true)}
              style={{ 
                borderRadius: "30px", 
                fontWeight: 600,
                padding: "0 24px",
                height: 44,
                boxShadow: "0 4px 10px rgba(233, 87, 53, 0.3)",
                display: "flex",
                alignItems: "center",
                gap: 8
              }}
            >
              Nueva Promoción
            </Button>
          </Space>
        </div>

        <TablePromotions
          promotions={filteredPromotions}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={removePromotion}
          onToggleStatus={updateStatus}
        />

        {/* Drawer para crear promoción */}
        <Drawer
          title={<span style={{ fontSize: 18, fontWeight: 600 }}>Crear Nueva Promoción</span>}
          open={openCreateDrawer}
          width={600}
          destroyOnClose
          onClose={closeCreateDrawer}
          headerStyle={{ borderBottom: "1px solid #f0f0f0", padding: "16px 24px" }}
          bodyStyle={{ padding: "24px", background: "#fafafa" }}
        >
          <PromotionForm
            setOpenModal={setOpenCreateDrawer}
            onSuccess={refresh}
          />
        </Drawer>

        {/* Drawer para editar promoción */}
        <Drawer
          title={
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              Editar: {editingPromotion?.name}
            </span>
          }
          open={Boolean(editingPromotion)}
          width={600}
          destroyOnClose
          onClose={closeEditDrawer}
          headerStyle={{ borderBottom: "1px solid #f0f0f0", padding: "16px 24px" }}
          bodyStyle={{ padding: "24px", background: "#fafafa" }}
        >
          {editingPromotion && (
            <PromotionForm
              setOpenModal={(open) => {
                if (!open) closeEditDrawer();
              }}
              onSuccess={() => {
                refresh();
                closeEditDrawer();
              }}
              promotion={editingPromotion}
            />
          )}
        </Drawer>

        {/* Modal para ver detalles */}
        <Modal
          title={<span style={{ fontSize: 18, fontWeight: 600 }}>Detalles: {selectedPromotion?.name || ""}</span>}
          open={viewModalOpen}
          onCancel={() => setViewModalOpen(false)}
          footer={null}
          width={700}
          bodyStyle={{ paddingTop: 16 }}
        >
          {selectedPromotion && (
            <>
              <Descriptions bordered column={2} size="small" style={{ marginBottom: 24 }}>
                <Descriptions.Item label="Nombre" span={2}>
                  <strong>{selectedPromotion.name}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Tipo">
                  <Tag color={selectedPromotion.promo_type === "2x1" ? "blue" : "purple"}>
                    {PROMO_TYPE_LABELS[selectedPromotion.promo_type]}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Estado">
                  <Tag color={selectedPromotion.status === "active" ? "green" : "default"}>
                    {selectedPromotion.status === "active" ? "Activo" : "Inactivo"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Días disponibles" span={2}>
                  {getDaysActive(selectedPromotion).length > 0
                    ? getDaysActive(selectedPromotion).join(", ")
                    : "Ninguno"}
                </Descriptions.Item>
                {selectedPromotion.description && (
                  <Descriptions.Item label="Descripción" span={2}>
                    {selectedPromotion.description}
                  </Descriptions.Item>
                )}
              </Descriptions>

              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: "#262626" }}>
                Categorías Configuradas
              </h3>

              {selectedPromotion.rule_categories && selectedPromotion.rule_categories.length > 0 ? (
                <List
                  dataSource={selectedPromotion.rule_categories}
                  renderItem={(cat) => (
                    <List.Item style={{ padding: "12px 16px", background: "#fafafa", borderRadius: 8, marginBottom: 8, border: "1px solid #f0f0f0" }}>
                      <List.Item.Meta
                        title={
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Tag color={cat.role === "primary" ? "blue" : "green"} style={{ margin: 0 }}>
                              {cat.role === "primary" ? "Principal" : "Secundaria"}
                            </Tag>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>
                              {cat.category?.name || `Categoría ID: ${cat.category_id}`}
                            </span>
                          </div>
                        }
                        description={
                          <span style={{ color: "#595959", marginTop: 4, display: "inline-block" }}>
                            Cantidad requerida: <strong>{cat.required_quantity}</strong> producto(s)
                          </span>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <Text type="secondary">No hay categorías configuradas</Text>
              )}

              <h3 style={{ fontSize: 16, fontWeight: 600, margin: "24px 0 16px", color: "#262626" }}>
                Productos elegibles para ítem gratis
              </h3>

              {selectedPromotion.free_products &&
              selectedPromotion.free_products.length > 0 ? (
                <List
                  dataSource={selectedPromotion.free_products}
                  renderItem={(item) => (
                    <List.Item
                      style={{
                        padding: "10px 16px",
                        background: "#f6ffed",
                        borderRadius: 8,
                        marginBottom: 8,
                        border: "1px solid #b7eb8f",
                      }}
                    >
                      <Tag color="green" style={{ marginRight: 8 }}>
                        Slot gratis
                      </Tag>
                      {item.product?.name || `Producto #${item.product_id}`}
                    </List.Item>
                  )}
                />
              ) : (
                <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                  Todos los productos fuera de la categoría principal
                </Text>
              )}

              <h3 style={{ fontSize: 16, fontWeight: 600, margin: "24px 0 16px", color: "#262626" }}>
                Productos excluidos (tarifa plena)
              </h3>

              {selectedPromotion.excluded_products &&
              selectedPromotion.excluded_products.length > 0 ? (
                <List
                  dataSource={selectedPromotion.excluded_products}
                  renderItem={(item) => (
                    <List.Item
                      style={{
                        padding: "10px 16px",
                        background: "#fff7e6",
                        borderRadius: 8,
                        marginBottom: 8,
                        border: "1px solid #ffd591",
                      }}
                    >
                      <Tag color="orange" style={{ marginRight: 8 }}>
                        No aplica promo
                      </Tag>
                      {item.product?.name || `Producto #${item.product_id}`}
                    </List.Item>
                  )}
                />
              ) : (
                <Text type="secondary">Ningún producto excluido</Text>
              )}
            </>
          )}
        </Modal>
      </Card>
    </AdminMenuLayout>
  );
};
